import { Fingering } from 'Types';
import { fingerNumberToDraw } from 'Fingering/display';
import { FINGER_NUMBER_FONT_FAMILY, fingerNumberFontSize } from 'Fingering/fingerNumberStyle';
import IPlayable from '../Playable';
import { ProgressState } from '../Progress';
import { Scene } from 'phaser';
import { Config } from './Types';
import { StaffTransition } from '.';
import ClefMeasure from './Measure';
import { getColorByCbType } from 'Components/Colors';
import { COLORBLIND_TYPE, ERROR_REC_CLASS } from 'Utils/Constants';
import Clef from './Clef';
import type { Box, Nudge } from './fingerNumberPlacement';

interface NoteTransitionState {
  stemHeight?: number;
  headScale: number;
  dotScale?: number;
  ledgerLineWidth?: number;
}

export type AccidentalSymbol = '#' | 'b' | 'n' | '##';

/**
 * Texture keys for the accidental SVG assets (Bravura, SIL OFL 1.1), loaded in
 * Preloader.ts and OSMDSightReadingScene.ts alongside the clefs. Accidentals now
 * render through the same `scene.add.image` asset system as every other glyph.
 */
const ACCIDENTAL_TEXTURE_KEY: Record<AccidentalSymbol, string> = {
  '#': 'accidental_sharp',
  b: 'accidental_flat',
  n: 'accidental_natural',
  '##': 'accidental_double_sharp',
};

/**
 * Per-glyph metrics for the Bravura accidental assets. The SVGs bake the SMuFL
 * registration point (the point that sits on the note's staff line/space) at the
 * vertical center of their viewBox, so a default center-origin placement lands the
 * glyph on the pitch line with no vertical offset to compute.
 *
 * - `heightInStaffSpaces`: the full image height, in staff spaces, at natural size
 *   (viewBox is padded symmetrically about the baseline, so this exceeds the visible
 *   glyph height for the flat, whose stem rises well above the registration point).
 * - `widthInStaffSpaces`: the glyph width in staff spaces (image width is tight in x).
 * - `xOffset`: inline accidental horizontal placement, in note-head widths, left of
 *   the note head it modifies.
 */
export const ACCIDENTAL_RENDERING: Record<
  AccidentalSymbol,
  {
    heightInStaffSpaces: number;
    widthInStaffSpaces: number;
    xOffset: number;
  }
> = {
  '#': {
    heightInStaffSpaces: 2.8,
    widthInStaffSpaces: 0.996,
    xOffset: 1.16,
  },
  b: {
    heightInStaffSpaces: 3.512,
    widthInStaffSpaces: 0.904,
    xOffset: 1.08,
  },
  n: {
    heightInStaffSpaces: 2.728,
    widthInStaffSpaces: 0.672,
    xOffset: 1.08,
  },
  '##': {
    heightInStaffSpaces: 1.024,
    widthInStaffSpaces: 0.992,
    xOffset: 1.16,
  },
};

const ACCIDENTAL_SYMBOLS: AccidentalSymbol[] = ['#', 'b', 'n', '##'];

/**
 * The glyphs drawn for a symbol. A double sharp whose picture did not load is
 * drawn as two sharps on the same line or space, not as the engine's
 * missing-picture box.
 */
export const accidentalGlyphsToDraw = (
  symbol: AccidentalSymbol,
  isTextureLoaded: (textureKey: string) => boolean,
): AccidentalSymbol[] =>
  symbol === '##' && !isTextureLoaded(ACCIDENTAL_TEXTURE_KEY['##']) ? ['#', '#'] : [symbol];

export const getAccidentalRequiredLeftPadding = (
  noteHeadWidth: number,
  noteHeadHeight: number,
  clearance: number,
): number =>
  Math.max(
    ...ACCIDENTAL_SYMBOLS.map((symbol) => {
      const rendering = ACCIDENTAL_RENDERING[symbol];
      // noteHeadHeight is one staff space, so glyph width (in staff spaces) scales to px against it.
      const accidentalWidth = noteHeadHeight * rendering.widthInStaffSpaces;

      return noteHeadWidth * rendering.xOffset + accidentalWidth / 2 + clearance;
    }),
  );

/**
 * A sharp / flat / natural / double sharp rendered from its Bravura SVG asset via `scene.add.image`
 * - the same asset system every other staff glyph (clefs, note heads, rests, time-sig
 * digits) already uses. The SVG bakes the SMuFL registration point at the center of its
 * viewBox, so the container's position IS the pitch line - there is no glyph-origin
 * offset to compute. Coloring is a tint fill, matching the note head.
 */
export class AccidentalGlyphGraphic extends Phaser.GameObjects.Container {
  private readonly glyphs: AccidentalSymbol[];
  private readonly images: Phaser.GameObjects.Image[];

  constructor(
    scene: Scene,
    symbol: AccidentalSymbol,
    private notationColor: number,
  ) {
    super(scene, 0, 0);
    this.glyphs = accidentalGlyphsToDraw(symbol, (textureKey) => scene.textures.exists(textureKey));
    this.images = this.glyphs.map((glyph) => scene.add.image(0, 0, ACCIDENTAL_TEXTURE_KEY[glyph]));
    this.images.forEach((image) => image.setTintFill(this.notationColor));
    this.add(this.images);
    this.syncSize();
  }

  resizeToNoteHead(noteHead: Phaser.GameObjects.Image) {
    // noteHead.displayHeight is one staff space; scale the glyph to its natural size in staff spaces.
    this.resizeToHeight(
      noteHead.displayHeight * ACCIDENTAL_RENDERING[this.glyphs[0]].heightInStaffSpaces,
    );
  }

  resizeToHeight(height: number) {
    this.images.forEach((image, place) => {
      if (image.height > 0) {
        // Uniform scale: the SVG carries the glyph's intrinsic aspect ratio and registration.
        image.setScale(height / image.height);
      }
      // A second glyph stands left of the first, which stays where a single one goes.
      image.setX(-place * image.displayWidth);
    });
    this.syncSize();
  }

  setNotationColor(color: number) {
    this.notationColor = color;
    this.images.forEach((image) => image.setTintFill(color));
  }

  private syncSize() {
    this.setSize(
      this.images.reduce((width, image) => width + image.displayWidth, 0),
      this.images[0].displayHeight,
    );
  }

  destroy(fromScene?: boolean) {
    super.destroy(fromScene);
  }
}

const getAccidentalPosition = (
  symbol: AccidentalSymbol,
  noteHead: Phaser.GameObjects.Image,
): { x: number; y: number } => {
  const rendering = ACCIDENTAL_RENDERING[symbol];

  return {
    // The SVG bakes the SMuFL registration point at the glyph's vertical center, so the
    // accidental sits on the note head's pitch line with no vertical offset to compute.
    x: noteHead.x - noteHead.displayWidth * rendering.xOffset,
    y: noteHead.y,
  };
};

export class NoteGraphic extends Phaser.GameObjects.Container implements IPlayable {
  isRest = false;
  idx: number;
  resetTimer?: Phaser.Time.TimerEvent;
  isFocused: boolean = false;
  timestamp?: number;
  measureIdx: number;
  timingState?: ProgressState;
  noteHead!: Phaser.GameObjects.Image;
  stem?: Phaser.GameObjects.Rectangle;
  ledgerLines: Phaser.GameObjects.Rectangle[] = [];
  noteLength: number;
  id: string;
  tweenHandles: Phaser.Tweens.Tween[] = [];
  noteDot?: Phaser.GameObjects.Arc;
  pulseObject?: Phaser.GameObjects.Arc;
  pulseTween?: Phaser.Tweens.Tween;
  accidentalGraphic?: AccidentalGlyphGraphic;
  accidentalSymbol?: AccidentalSymbol;
  originalPosition: number;
  fingering?: Fingering;
  /** 0-100 need score of a finger number computed in the app. */
  fingeringNeed?: number;
  fingeringGraphic?: Phaser.GameObjects.Text;
  fingeringGraphicDebug?: Phaser.GameObjects.Rectangle;
  /** Set to true to show debug rectangles for note bounds */
  showFingeringDebug: boolean = false;
  stemDirection: 'up' | 'down';

  transitionState?: NoteTransitionState;
  midiNote: number;

  line: number;
  unscaledX: number;

  innerContainer?: Phaser.GameObjects.Container;
  debugRectangle: Phaser.GameObjects.Rectangle | undefined;

  parentClef: Clef;

  /** DPR used for rendering. Inherited from parent Clef. */
  renderDpr: number;

  defaultColor: number;

  /** Saved state/color before demo timing state was applied; restored on restoreFromDemo(). */
  private savedDemoState?: { state?: ProgressState; color: number };

  constructor(
    scene: Scene,
    measureIdx: number,
    id: string,
    x: number,
    unscaledX: number,
    line: number,
    length: number,
    { pitchLineGap, pitchLineWidth }: Config,
    timestamp: number,
    idx: number,
    midiNote: number,
    parentClef: Clef,
    fingering?: Fingering,
    accidentalSymbol?: AccidentalSymbol,
    color?: number | string,
    fingeringNeed?: number,
  ) {
    super(scene);
    this.defaultColor = Phaser.Display.Color.ValueToColor(color ?? 0).color;
    this.parentClef = parentClef;
    this.renderDpr = parentClef.renderDpr;
    this.innerContainer = this.scene.add.container(0, 0, []);
    this.add(this.innerContainer);
    this.unscaledX = unscaledX;
    this.setX(x);
    this.line = line;
    this.midiNote = midiNote;
    this.measureIdx = measureIdx;
    this.idx = idx;
    this.fingering = fingering;
    this.fingeringNeed = fingeringNeed;
    this.accidentalSymbol = accidentalSymbol;
    this.timestamp = timestamp;
    this.noteLength = length;
    this.id = id;
    scene.add.existing(this);
    const yPos = (line / 2) * pitchLineGap;
    let dotted = false;
    switch (length) {
      case 0.25:
        this.noteHead = scene.add.image(0, yPos, 'quarter_note');
        break;
      case 0.3125:
        this.noteHead = scene.add.image(0, yPos, 'quarter_note');
        dotted = true;
        break;
      case 0.5:
        this.noteHead = scene.add.image(0, yPos, 'half_note');
        break;
      case 0.75:
        this.noteHead = scene.add.image(0, yPos, 'half_note');
        dotted = true;
        break;
      case 1:
        this.noteHead = scene.add.image(0, yPos, 'whole_note');
        break;
      case 1.5:
        this.noteHead = scene.add.image(0, yPos, 'whole_note');
        dotted = true;
        break;
      default:
        console.error('Only half, quarter and whole notes supported for now!');
    }
    this.noteHead.setTintFill(this.defaultColor);
    this.noteHead.setScale(pitchLineGap / this.noteHead.displayHeight);
    this.innerContainer.add(this.noteHead);
    if (this.accidentalSymbol) {
      const accidentalPosition = getAccidentalPosition(this.accidentalSymbol, this.noteHead);
      this.accidentalGraphic = new AccidentalGlyphGraphic(
        scene,
        this.accidentalSymbol,
        this.defaultColor,
      );
      this.accidentalGraphic.resizeToNoteHead(this.noteHead);
      this.accidentalGraphic.setPosition(accidentalPosition.x, accidentalPosition.y);
      this.innerContainer.add(this.accidentalGraphic);
    }
    if (dotted) {
      const dotX = this.noteHead.x + this.noteHead.displayWidth * 1.2;
      let yOffset = 0;
      const dotR = this.noteHead.displayWidth / 6;
      if (line % 2 === 0) {
        yOffset = this.noteHead.displayHeight / 3 + dotR / 2;
      }
      const dotY = this.noteHead.y - yOffset;
      this.noteDot = scene.add.circle(dotX, dotY, dotR, this.defaultColor, 1);
      this.innerContainer.add(this.noteDot);
    }
    const ledgerLineWidth = this.noteHead.displayWidth * 1.5;
    // The stem width should be relative to the width of the note head itself,
    // otherwise it doesn't scale properly along with everything else.
    const stemWidth = this.noteHead.displayWidth * 0.1;
    const stemHeight = pitchLineGap * 3.5;
    let stemYOffset = stemHeight / 2;
    let stemXOffset = -this.noteHead.displayWidth / 2 + stemWidth / 2;
    if (line >= 5) {
      this.stemDirection = 'up';
      stemYOffset *= -1;
      stemXOffset *= -1;
    } else {
      this.stemDirection = 'down';
    }
    this.stem = [0.25, 0.375, 0.5, 0.75].contains(length)
      ? scene.add.rectangle(stemXOffset, yPos + stemYOffset, stemWidth, stemHeight, 0, 1)
      : undefined;
    if (this.stem) this.innerContainer.add(this.stem);
    if (line < 0) {
      const numLedgerLines = Math.floor((0 - line) / 2);
      for (let i = 0; i < numLedgerLines; i++) {
        this.ledgerLines.push(
          scene.add.rectangle(
            0,
            (-i - 1) * pitchLineGap,
            ledgerLineWidth,
            pitchLineWidth * this.renderDpr,
            this.defaultColor,
            1,
          ),
        );
      }
    } else {
      const numLedgerLines = Math.floor((line - 8) / 2);
      for (let i = 0; i < numLedgerLines; i++) {
        this.ledgerLines.push(
          scene.add.rectangle(
            0,
            (i + 5) * pitchLineGap,
            ledgerLineWidth,
            pitchLineWidth * this.renderDpr,
            this.defaultColor,
            1,
          ),
        );
      }
    }
    this.ledgerLines.forEach((line) => {
      line.setAlpha(0);
      this.innerContainer?.add(line);
      this.sendToBack(line);
    });
    this.accidentalGraphic?.setAlpha(0);
    this.originalPosition = this.x;
    this.noteHead.alpha = 0;
    this.stem?.setAlpha(0);
    this.noteDot?.setAlpha(0);
    if (color !== undefined) this.setTintFill(this.defaultColor);
  }

  setParent(measure: ClefMeasure) {
    measure.add(this);
  }

  resize(pitchLineGap: number, xPosScale: number, _fullMeasureWidth: number) {
    this.setX(this.unscaledX * xPosScale);
    this.originalPosition = this.x;
    this.noteHead.setScale((pitchLineGap * this.renderDpr) / this.noteHead.height);
    this.noteHead.setY((this.line / 2) * pitchLineGap * this.renderDpr);
    if (this.accidentalGraphic && this.accidentalSymbol) {
      const accidentalPosition = getAccidentalPosition(this.accidentalSymbol, this.noteHead);
      this.accidentalGraphic.resizeToNoteHead(this.noteHead);
      this.accidentalGraphic.setPosition(accidentalPosition.x, accidentalPosition.y);
    }
    //this.noteHead.setX(xStartOffset + this.unscaledX * xPosScale);
    const dotX = this.noteHead.x + this.noteHead.displayWidth * 1.2;
    let yOffset = 0;
    const dotR = this.noteHead.displayWidth / 6;
    if (this.line % 2 === 0) {
      yOffset = this.noteHead.displayHeight / 3 + dotR / 2;
    }
    const dotY = this.noteHead.y - yOffset;
    this.noteDot?.setX(dotX);
    this.noteDot?.setY(dotY);
    this.noteDot?.setRadius(dotR);
    const ledgerLineWidth = this.noteHead.displayWidth * 1.5;
    // The stem width should be relative to the width of the note head itself,
    // otherwise it doesn't scale properly along with everything else.
    const stemWidth = this.noteHead.displayWidth * 0.1;
    const stemHeight = pitchLineGap * this.renderDpr * 3.5;
    let stemYOffset = stemHeight / 2;
    let stemXOffset = -this.noteHead.displayWidth / 2 + stemWidth / 2;
    if (this.stemDirection === 'up') {
      stemYOffset *= -1;
      stemXOffset *= -1;
    }
    this.stem?.setX(this.noteHead.x + stemXOffset);
    this.stem?.setY((this.line / 2) * pitchLineGap * this.renderDpr + stemYOffset);
    this.stem?.setSize(stemWidth, stemHeight);
    this.ledgerLines.forEach((l, i) => {
      l.setSize(ledgerLineWidth, l.height);
      if (this.line < 0) l.setY((-i - 1) * pitchLineGap * this.renderDpr);
      else l.setY((i + 5) * pitchLineGap * this.renderDpr);
    });
  }

  getY() {
    return this.noteHead.y;
  }

  transitionOut() {
    this.addTween({
      targets: [
        ...[this.noteHead],
        ...[this.accidentalGraphic],
        ...[this.noteDot],
        ...[this.stem],
        ...[this.fingeringGraphic],
        ...this.ledgerLines,
      ],
      alpha: 0,
      duration: 500,
      ease: 'Power2',
    });
  }

  transition(style: StaffTransition) {
    switch (style) {
      case 'Draw': {
        const transitionState = {
          stemHeight: this.stem?.height,
          headScale: this.noteHead.scale,
          fingeringScale: this.fingeringGraphic?.scale,
          dotScale: this.noteDot?.scale,
          ledgerLineWidth: this.ledgerLines[0]?.width,
          stemY: this.stem?.y,
        };
        this.ledgerLines.forEach((line) => (line.width = 0));
        this.noteHead.setScale(0);
        this.fingeringGraphic?.setScale(0);
        this.noteDot?.setScale(0);
        this.accidentalGraphic?.setAlpha(0);
        if (this.stem) {
          this.stem.setY(
            this.stem.y + this.stem.displayHeight * (this.stemDirection === 'up' ? 1 : 0),
          );
          this.stem.height = 0;
        }
        this.addTween({
          targets: [this.noteHead],
          alpha: 1,
          scale: transitionState.headScale,
          duration: 500,
          ease: 'Power2',
          onComplete: () => {
            this.addTween({
              targets: [...this.ledgerLines],
              alpha: 1,
              width: transitionState.ledgerLineWidth,
              duration: 500,
            });
            if (this.stem)
              this.addTween({
                targets: [this.stem],
                alpha: 1,
                y: transitionState.stemY,
                height: transitionState.stemHeight,
                duration: 500,
                ease: 'Power2',
              });
            if (this.fingeringGraphic)
              this.addTween({
                targets: [this.fingeringGraphic],
                alpha: 1,
                scale: transitionState.fingeringScale,
                duration: 500,
                ease: 'Power2',
              });
            if (this.noteDot)
              this.addTween({
                targets: [this.noteDot],
                scale: transitionState.dotScale,
                alpha: 1,
                duration: 500,
                ease: 'Power2',
              });
            if (this.accidentalGraphic)
              this.addTween({
                targets: [this.accidentalGraphic],
                alpha: 1,
                duration: 500,
                ease: 'Power2',
              });
          },
        });
        break;
      }
      case 'None':
        this.ledgerLines.forEach((line) => (line.alpha = 1));
        this.noteHead.setAlpha(1);
        this.accidentalGraphic?.setAlpha(1);
        this.noteDot?.setAlpha(1);
        this.fingeringGraphic?.setAlpha(1);
        this.stem?.setAlpha(1);
        break;
      case 'Fade':
        this.addTween({
          targets: [
            ...[this.noteHead],
            ...[this.accidentalGraphic],
            ...[this.noteDot],
            ...[this.stem],
            ...this.ledgerLines,
            ...[this.fingeringGraphic],
          ],
          alpha: 1,
          duration: 500,
          ease: 'Power2',
        });
        break;
    }
  }

  addTween(tween: Phaser.Tweens.Tween | Phaser.Types.Tweens.TweenBuilderConfig) {
    this.tweenHandles.push(this.scene.tweens.add(tween));
  }

  update(_: number) {}

  destroy() {
    this.tweenHandles.forEach((tween) => tween.remove());
    this.resetTimer?.remove();
    this.isFocused = false;
    this.fingeringGraphic?.destroy();
    this.stem?.destroy();
    this.ledgerLines.forEach((l) => l.destroy());
    this.ledgerLines.clear();
    this.noteHead.destroy();
    this.accidentalGraphic?.destroy();
    this.pulseTween?.stop();
    this.pulseObject?.destroy();
    this.noteDot?.destroy();
    super.destroy();
  }

  setFocus(focus: boolean) {
    this.isFocused = focus;
    this.tweenHandles.forEach((tween) => tween.remove());
  }

  showFingering(
    parent: Phaser.GameObjects.Container,
    max: number,
    min: number,
    maxNote: NoteGraphic,
    minNote: NoteGraphic,
    staffPosition: 'top' | 'bottom',
    pitchLineGap: number,
    fingeringMinimumNeed?: number,
  ) {
    const bounds = this.getClefRelativeBounds();
    if (fingeringMinimumNeed !== undefined) {
      this.syncFingeringGraphic(
        fingerNumberToDraw(this.fingering, this.fingeringNeed, fingeringMinimumNeed),
        staffPosition === 'top' ? max : min,
        pitchLineGap,
      );
    } else if (this.fingering) {
      if (!this.fingeringGraphic) {
        const y = staffPosition === 'top' ? max : min;
        const x = (bounds.x2 - bounds.x1) / 2;
        if (this.fingering)
          this.fingeringGraphic = this.scene.add.text(x, y, this.fingering.value, {
            color: Phaser.Display.Color.ValueToColor(this.defaultColor).rgba,
            fontSize: fingerNumberFontSize(pitchLineGap) * this.renderDpr,
            fontFamily: FINGER_NUMBER_FONT_FAMILY,
          });
      } else {
        const y = staffPosition === 'top' ? max : min;
        const _x = (bounds.x2 - bounds.x1) / 2;
        this.remove(this.fingeringGraphic);
        this.fingeringGraphic.setFontSize(fingerNumberFontSize(pitchLineGap) * this.renderDpr);
        this.fingeringGraphic.setPosition(
          0,
          y /*+
            ((staffPosition === "top" ? -1 : 1) *
              pitchLineGap *
              window.devicePixelRatio) /
              2*/,
        );
        console.debug('bounds', bounds, min, y);
        this.add(this.fingeringGraphic);
        /*if (!this.debugRectangle) {
          this.debugRectangle = this.scene.add.rectangle(
            0,
            0,
            100,
            100,
            0x0000ff,
            0.5,
          );
          this.add(this.debugRectangle);
        }*/
        //this.debugRectangle.setPosition(0, y);
      }
    }
    // Debug rectangles - set showFingeringDebug to true to visualize note bounds
    if (this.showFingeringDebug) {
      if (!this.fingeringGraphicDebug) {
        const x = bounds.x1;
        const width = bounds.x2 - bounds.x1;
        const height = bounds.y2 - bounds.y1;
        // eslint-disable-next-line no-constant-condition, no-constant-binary-expression -- debug conditional for fingering bounds
        if (true || maxNote == this || minNote == this) {
          this.fingeringGraphicDebug = this.scene.add.rectangle(
            x,
            min,
            width,
            height,
            maxNote == this ? 0xff0000 : 0x0000ff,
            0.5,
          );
          this.add(this.fingeringGraphicDebug!);
        }
      } else {
        const x = bounds.x1;
        const width = bounds.x2 - bounds.x1;
        this.fingeringGraphicDebug?.setSize(width, 10);
        this.fingeringGraphicDebug.setOrigin(0, 0);
        this.fingeringGraphicDebug.setPosition(x, max);
        this.fingeringGraphicDebug.setFillStyle(maxNote == this ? 0xff0000 : 0x0000ff, 0.5);
      }
    }
    if (staffPosition === 'top' && this.fingeringGraphic) this.fingeringGraphic.setOrigin(0.5, 1);
    else this.fingeringGraphic?.setOrigin(0.5, 0);
    if (this.fingeringGraphic) this.add(this.fingeringGraphic);
    //parent.add(this.fingeringGraphic);
  }

  /** Make the drawn finger number match `text`: create it, re-text it or remove it. */
  private syncFingeringGraphic(text: string | undefined, y: number, pitchLineGap: number) {
    if (text === undefined) {
      this.fingeringGraphic?.destroy();
      this.fingeringGraphic = undefined;
      return;
    }
    const fontSize = fingerNumberFontSize(pitchLineGap) * this.renderDpr;
    if (!this.fingeringGraphic) {
      this.fingeringGraphic = this.scene.add.text(0, y, text, {
        fontSize,
        fontFamily: FINGER_NUMBER_FONT_FAMILY,
      });
      // Notes are built invisible and faded in; a number added to a note that
      // is already on screen has no fade left to ride.
      this.fingeringGraphic.setAlpha(this.noteHead.alpha > 0 ? 1 : 0);
    } else {
      this.fingeringGraphic.setText(text);
      this.fingeringGraphic.setFontSize(fontSize);
    }
    this.fingeringGraphic.setColor(Phaser.Display.Color.ValueToColor(this.defaultColor).rgba);
    this.fingeringGraphic.setPosition(this.noteHead.x, y);
  }

  /** A box of this note's own coordinates, as it lies in the scene. */
  private inScene(box: Box): Box {
    const { a: scaleX, d: scaleY, tx, ty } = this.getWorldTransformMatrix();
    return {
      left: tx + box.left * scaleX,
      top: ty + box.top * scaleY,
      right: tx + box.right * scaleX,
      bottom: ty + box.bottom * scaleY,
    };
  }

  /** Everything drawn of this note, as boxes in the scene: what a finger
   *  number must not sit on. */
  getDrawnBoxes(): Box[] {
    const centred = (part: { x: number; y: number }, width: number, height: number): Box => ({
      left: part.x - width / 2,
      top: part.y - height / 2,
      right: part.x + width / 2,
      bottom: part.y + height / 2,
    });
    const sized = (part: { x: number; y: number; displayWidth: number; displayHeight: number }) =>
      centred(part, part.displayWidth, part.displayHeight);
    // Ledger lines are drawn in growing: their full width is the one that counts.
    const ledgerLineWidth = this.noteHead.displayWidth * 1.5;
    return [
      sized(this.noteHead),
      ...(this.stem ? [sized(this.stem)] : []),
      ...(this.accidentalGraphic ? [sized(this.accidentalGraphic)] : []),
      ...(this.noteDot ? [sized(this.noteDot)] : []),
      ...this.ledgerLines.map((line) =>
        centred({ x: this.noteHead.x, y: line.y }, ledgerLineWidth, line.displayHeight),
      ),
    ].map((box) => this.inScene(box));
  }

  /** The drawn finger number's box in the scene; undefined when the note shows none. */
  getFingerNumberBox(): Box | undefined {
    const text = this.fingeringGraphic;
    if (!text) return undefined;
    const left = text.x - text.displayWidth * text.originX;
    const top = text.y - text.displayHeight * text.originY;
    return this.inScene({
      left,
      top,
      right: left + text.displayWidth,
      bottom: top + text.displayHeight,
    });
  }

  /** Move the drawn finger number by a distance measured in the scene. */
  nudgeFingerNumber({ dx, dy }: Nudge) {
    const text = this.fingeringGraphic;
    if (!text) return;
    const { a: scaleX, d: scaleY } = this.getWorldTransformMatrix();
    text.setPosition(text.x + dx / scaleX, text.y + dy / scaleY);
  }

  // Returns the bounds of the note relative to the highest line in the clef
  getClefRelativeBounds() {
    const noteHeadHeight = this.noteHead.displayHeight;
    const noteHeadWidth = this.noteHead.displayWidth;
    const _dotSize = this.noteDot ? this.noteDot.displayWidth : 0;
    const _dotXOffset = this.noteDot ? this.noteDot.x - this.noteHead.x : 0;
    //const height = noteHeadHeight + stemHeight;
    let x1 = this.noteHead.x - noteHeadWidth / 2;
    let x2 = this.noteHead.x + noteHeadWidth / 2;
    if (this.noteDot) {
      x2 = this.noteDot.x + this.noteDot.displayWidth / 2;
    }
    if (this.accidentalGraphic) {
      x1 = Math.min(x1, this.accidentalGraphic.x - this.accidentalGraphic.displayWidth / 2);
    }
    let y1 = this.noteHead.y - noteHeadHeight / 2;
    let y2 = this.noteHead.y + noteHeadHeight / 2;
    if (this.stem) {
      if (this.stemDirection === 'up') {
        y1 -= this.stem.displayHeight - noteHeadHeight / 2;
      } else {
        y2 += this.stem.displayHeight - noteHeadHeight / 2;
      }
    }
    return {
      x1,
      y1,
      x2,
      y2,
    };
  }

  setTintFill(color: number) {
    if (this.stem) this.stem.fillColor = color;
    this.noteHead.setTintFill(color);
    this.ledgerLines.forEach((line) => line.setFillStyle(color));
    if (this.noteDot) this.noteDot.fillColor = color;
    if (this.accidentalGraphic) {
      this.accidentalGraphic.setNotationColor(color);
    }
  }

  /**
   * Return the current display color as a numeric tint (for save/restore).
   * Uses noteHead tint when set; falls back to defaultColor.
   */
  getColor(): number {
    const t = this.noteHead.tintTopLeft;
    return typeof t === 'number' && !Number.isNaN(t) ? t : this.defaultColor;
  }

  /**
   * Set timing state for app tour demo: save current state/color, then apply the given state.
   * Restore later with restoreFromDemo().
   */
  setDemoTimingState(state: ProgressState, colorblindType?: COLORBLIND_TYPE | null): void {
    if (!this.savedDemoState)
      this.savedDemoState = {
        state: this.timingState,
        color: this.getColor(),
      };
    this.setTimingState(state, colorblindType);
  }

  /**
   * Restore note to state/color saved by setDemoTimingState(). No-op if nothing was saved.
   */
  restoreFromDemo(): void {
    if (this.savedDemoState === undefined) return;
    this.timingState = this.savedDemoState.state;
    console.debug('[tour] restoreFromDemo', this.savedDemoState);
    this.setTintFill(this.savedDemoState.color);
    this.savedDemoState = undefined;
  }

  setTimingState(state: ProgressState, colorblindType?: COLORBLIND_TYPE | null) {
    this.tweenHandles.forEach((tween) => tween.seek(tween.duration));
    //this.tweenHandles.clear();
    console.debug('[Colorblind] setTimingState', state, colorblindType);
    // Prevent a late-onset state from downgrading a note that was already correctly played.
    // MistimedRelease is intentionally excluded from this guard because it represents a
    // legitimate offset timing error (e.g. early release) that should still show yellow.
    const isLateOnsetDowngrade = state === ProgressState.LateStart;
    if (this.timingState === ProgressState.Ended && isLateOnsetDowngrade) {
      return;
    }
    this.timingState = state;
    switch (state) {
      case ProgressState.LateStart:
      case ProgressState.MistimedRelease: {
        // Use colorblind-aware color for semi-error (yellow/orange/grey)
        const semiErrorColor = getColorByCbType(ERROR_REC_CLASS.SEMI_ERROR, colorblindType ?? null);
        this.setColor(semiErrorColor);
        break;
      }
      case ProgressState.Ended: {
        // Use colorblind-aware color for correct (green/blue/white)
        const correctColor = getColorByCbType(ERROR_REC_CLASS.CORRECT, colorblindType ?? null);
        this.setColor(correctColor);
        break;
      }
      case ProgressState.Missed: {
        // Use colorblind-aware color for error (red/brown/black)
        const errorColor = getColorByCbType(ERROR_REC_CLASS.ERROR, colorblindType ?? null);
        this.setColor(errorColor);
        break;
      }
      default:
    }
  }

  getState() {
    return this.timingState;
  }

  shake() {
    this.tweenHandles.push(
      this.scene.tweens.add({
        targets: { value: 0 },
        value: Math.PI * 2,
        duration: 250,
        repeat: 1,
        onUpdate: (_tween, _target, _key, current) => {
          console.log(current);
          this.setX(this.originalPosition + Math.sin(current) * 10 * 1);
        },
      }),
    );
  }

  reset(force: boolean) {
    this.timingState = undefined;
    const cb = () => {
      this.setTintFill(this.defaultColor);
      this.pulseObject?.destroy();
      this.pulseObject = undefined;
      this.pulseTween?.remove();
      this.tweenHandles.forEach((tween) => tween.remove());
      this.setX(this.originalPosition);
    };
    /*this.addTween({
      targets: this.noteHead,
      tint: 0,
      duration: force ? 0 : 250,
      delay: force ? 0 : this.idx * 50 + 2000,
    });*/
    /*this.addTween({
      targets: [this.stem, ...this.ledgerLines, this.noteDot],
      fillColor: 0,
      duration: force ? 0 : 250,
      delay: force ? 0 : this.idx * 50 + 2000,
    });*/
    //};
    if (force) {
      this.resetTimer?.remove();
      cb.call(this);
    } else {
      this.resetTimer = this.scene.time.delayedCall(
        2000, //+ this.idx * 50,
        cb,
        [],
        this,
      );
    }
  }

  getPosition() {
    return { x: this.noteHead.x, y: this.noteHead.y };
  }

  getHaloOffset() {
    if (this.noteLength !== 1) {
      return this.height / 3.75;
    } else {
      return 0;
    }
  }

  pulse(colorblindType?: COLORBLIND_TYPE | null) {
    // Get colorblind-aware correct color for pulse
    const correctColorHex = getColorByCbType(ERROR_REC_CLASS.CORRECT, colorblindType ?? null);
    const correctColor = Phaser.Display.Color.ValueToColor(correctColorHex).color;

    if (!this.pulseObject) {
      this.pulseObject = this.scene.add.circle(
        this.noteHead.x,
        this.noteHead.y,
        0,
        correctColor,
        1,
      );
      this.add(this.pulseObject);
    } else {
      // Update color if pulse object already exists
      this.pulseObject.setFillStyle(correctColor);
    }
    this.pulseTween = this.scene.tweens.add({
      targets: this.pulseObject,
      radius: this.noteHead.displayWidth * 2,
      alpha: 0,
      duration: 750,
      onComplete: () => {
        this.pulseObject?.setAlpha(0);
      },
      ease: 'Power2',
      callbackScope: this,
    });
  }

  setColor(color: string | number) {
    this.setTintFill(Phaser.Display.Color.ValueToColor(color).color);
  }

  resetColor() {
    this.setTintFill(this.defaultColor);
  }

  setAlpha(value?: number | undefined): this {
    this.noteDot?.setAlpha(value);
    this.noteHead.setAlpha(value);
    this.accidentalGraphic?.setAlpha(value);
    this.ledgerLines.forEach((l) => l.setAlpha(value));
    this.stem?.setAlpha(value);
    this.fingeringGraphic?.setAlpha(value);
    return this;
  }

  getBoundsY() {
    return (
      this.getClefRelativeBounds().y1 +
      Math.abs(this.getClefRelativeBounds().y2 - this.getClefRelativeBounds().y1) / 2
    );
  }
}

export default NoteGraphic;
